import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { PackageDetail } from '../../_common/interfaces'
import { Dayjs } from 'dayjs'
import { mercuryApi } from '$api'
import useAppStore from '$appStore'
import { exceptionMessageHandler, PermissionType } from '$utils'
import {
  IClockinResponse,
  IRosteredShiftResponse,
  IStaffMemberDetail,
} from '$models'
import { ShiftType } from '$constants'
import {
  rosteredShiftService,
  staffMemberService,
  timeClockinService,
} from '$services'

interface PaySummaryState {
  clockins: IClockinResponse[]
  rosteredShifts: IRosteredShiftResponse[]
  staffMembers: IStaffMemberDetail[]
  allPackages: PackageDetail[]
}

export interface PaySummarySlice extends PaySummaryState {
  resetPaySummarySlice: () => void

  getClockins: (
    startDateTime: Dayjs,
    endDateTime: Dayjs
  ) => Promise<IClockinResponse[]>

  getRosteredShifts: (startDateTime: Dayjs, endDateTime: Dayjs) => Promise<void>

  getStaffMembers: (time?: Dayjs, from?: string, to?: string) => Promise<void>

  getPackages: () => Promise<void>
}

const initialState: PaySummaryState = {
  clockins: [],
  rosteredShifts: [],
  staffMembers: [],
  allPackages: [],
}

const createPaySummarySlice: StateCreator<
  PaySummarySlice & SharedSlice,
  [],
  [],
  PaySummarySlice
> = (set) => ({
  ...initialState,

  resetPaySummarySlice: () => set(initialState),

  getClockins: async (
    startDateTime: Dayjs,
    endDateTime: Dayjs
  ): Promise<IClockinResponse[]> => {
    return timeClockinService
      .getClockins(
        startDateTime,
        endDateTime,
        false,
        [ShiftType.Clockin, ShiftType.Roster, ShiftType.DefaultHours],
        undefined,
        false,
        true,
        true
      )
      .then((result) => {
        set(() => ({ clockins: result }))
        return result
      })
      .catch((error) => {
        set(() => ({ clockins: [] }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve pay summary clockins'
        )
        return []
      })
  },

  getRosteredShifts: async (
    startDateTime: Dayjs,
    endDateTime: Dayjs
  ): Promise<void> => {
    return rosteredShiftService
      .getAll(
        startDateTime,
        endDateTime,
        undefined,
        false,
        true,
        true,
        undefined,
        true
      )
      .then((result) => set(() => ({ rosteredShifts: result })))
      .catch((error) => {
        set(() => ({ rosteredShifts: [] }))
        exceptionMessageHandler(error, 'Failed to retrieve rostered shifts')
      })
  },

  getStaffMembers: async (
    time?: Dayjs,
    from?: string,
    to?: string
  ): Promise<void> => {
    return staffMemberService
      .getAll(time, true, false, from, to)
      .then((result) => set(() => ({ staffMembers: result })))
      .catch((error) => {
        set(() => ({ staffMembers: [] }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
  },

  getPackages: async (): Promise<void> => {
    const { activeCompany } = useAppStore.getState()

    const hasPayrollPermission = activeCompany?.hasPermission(
      PermissionType.Payroll
    )

    if (!hasPayrollPermission) {
      return set(() => ({ allPackages: [] }))
    }

    return mercuryApi
      .get<PackageDetail[]>(`/pay-rules/packages`)
      .then((result) => set(() => ({ allPackages: result })))
      .catch((error) => {
        set(() => ({ allPackages: [] }))
        exceptionMessageHandler(error, 'Failed to retrieve packages')
      })
  },
})

export default createPaySummarySlice
